import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import "./App.css";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Stats from "./components/Stats";
import HowItWorks from "./components/HowItWorks";
import WhyUse from "./components/WhyUse";
import WhoFor from "./components/WhoFor";
import Testimonials from "./components/Testimonials";
import FAQ from "./components/FAQ";
import ContactForm from "./components/ContactForm";
import AgentJoinForm from "./components/AgentJoinForm";
import Footer from "./components/Footer";
import ChatWidget from "./components/ChatWidget";
import { Toaster } from "./components/ui/sonner";
import { isAuthenticated } from "./lib/adminAPI";

// Admin pages
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import AdminContacts from "./pages/AdminContacts";
import AdminAgents from "./pages/AdminAgents";
import AdminChats from "./pages/AdminChats";
import ContactDetail from "./pages/ContactDetail";
import AgentDetail from "./pages/AgentDetail";
import ThankYou from "./pages/ThankYou";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import AgentFeesGuide from "./pages/AgentFeesGuide";
import { LANDING_PAGES, LANDING_PATHS, canonicalFor } from "./data/landingPages";
import { usePageSeo, useScrollToSection } from "./lib/seo";

// Protected route component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
};

const DEFAULT_SEO = {
  title: "FindMeAgent – Compare Top Local Real Estate Agents, Free",
  description: "Connect with pre-vetted, licence-checked local real estate agents. Free shortlist of 2 to 3 agents in your suburb, no obligation.",
};

// Public home page (also used for sitelink landing routes with hero/SEO overrides)
const HomePage = ({ landing, path = "/" }) => {
  usePageSeo({
    title: landing?.title || DEFAULT_SEO.title,
    description: landing?.description || DEFAULT_SEO.description,
    canonical: canonicalFor(path),
  });
  useScrollToSection(landing?.sectionId);

  return (
  <>
    <Header />
    <main>
      <Hero landing={landing} />
      <Stats />
      <HowItWorks />
      <WhyUse />
      <Testimonials />
      <WhoFor />
      <FAQ />
      <AgentJoinForm />
      <ContactForm />
    </main>
    <Footer />
  </>
  );
};

function App() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
      language="en"
    >
      <BrowserRouter>
        <div className="App">
          <ChatWidget />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            {LANDING_PATHS.map((path) => (
              <Route key={path} path={path} element={<HomePage landing={LANDING_PAGES[path]} path={path} />} />
            ))}
            <Route path="/thank-you" element={<ThankYou />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/real-estate-agent-fees-australia" element={<AgentFeesGuide />} />
            
            {/* Admin routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route 
              path="/admin/dashboard" 
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/contacts" 
              element={
                <ProtectedRoute>
                  <AdminContacts />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/contacts/:id" 
              element={
                <ProtectedRoute>
                  <ContactDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/admin/agents" 
              element={
                <ProtectedRoute>
                  <AdminAgents />
                </ProtectedRoute>
              } 
            />
            <Route path="/admin/chats" element={<ProtectedRoute><AdminChats /></ProtectedRoute>} />
            <Route 
              path="/admin/agents/:id" 
              element={
                <ProtectedRoute>
                  <AgentDetail />
                </ProtectedRoute>
              } 
            />
            
            {/* Redirect /admin to /admin/dashboard */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </GoogleReCaptchaProvider>
  );
}

export default App;
